/**
 * feature-config flag names owned by code-gen
 *
 * please consider removing this file and adding it to an appropriate place in the
 * feature code (e.g. an entrypoint, or a package owned by the feature team), or
 * passing flag names directly as a string!
 *
 * by separating it out, we decouple the feature code from the feature config packages
 * and avoid the need to update this package when flags are added or removed.
 */

export const CODE_GEN_FLAG_NAMES = {
  AGENT_SERVICE: 'ff-aio-15-agent-service',
  // When ON, the agent service accepts first-class image ingestion: the
  // /agent-sessions/:sessionId/images upload route, agent-image refs as
  // images[].url (resolved to presigned GETs at dispatch), image-only turns,
  // and the data:/https + per-turn-cap hardening. Independent of AGENT_SERVICE
  // so ingestion has its own rollout curve and kill switch. Default OFF in prod.
  AGENT_SERVICE_IMAGE_INGESTION: 'ff-devrel-3009-agent-service-image-ingestion',
  AGENT_INSTRUCTIONS: 'ff-aio-227-agent-instructions', // https://console.statsig.com/7kzGQWd4hVXvdCs5dNkqUB/gates/ff-aio-227-agent-instructions
  AGENT_INSTRUCTIONS_SHARED_LIBRARIES:
    'ff-ext-632-agent-instructions-shared-libraries',
  // Gates per-library sections in the agent instructions reference insert
  // menu. Default OFF until lazy component/asset picks and polish land.
  AGENT_INSTRUCTIONS_LIBRARY_REFERENCES:
    'ff-ext-938-agent-instructions-library-references',
  // EXT-1143: gates on-the-fly agent-instruction generation. When ON, the
  // templated cards in the instruction creator open a generation modal instead
  // of writing fixed template markdown. Off by default.
  AGENT_INSTRUCTIONS_GENERATION: 'ff-ext-1143-agent-instructions-generation',
  // EXT-1725: gates AI-credit limit enforcement and usage reporting for
  // agent-instruction generation. Off in Statsig until billing rollout begins.
  AGENT_INSTRUCTIONS_GENERATION_AI_CREDITS:
    'ff-ext-1725-agent-instructions-generation-ai-credits',
  // AIO-211: gates the monolith ai-bridge minter between HS256 (off)
  // and RS256 (on). Code default is ON; Statsig per-env override
  // controls the actual rollout. PR-3 cleanup removes this flag once
  // RS256 is at 100% in prod and the in-flight HS256 token TTL has
  // elapsed.
  AI_BRIDGE_RS256_MINT: 'ff-aio-211-ai-bridge-rs256-mint',
  APP_GEN_AGENT_SERVICE: 'ff-aio-114-app-gen-agent-service',
  // Statsig kill-switch for the page-automation surfaces (public REST + MCP).
  // Successor to the retired devpl `ff-devpl-webflow-client` page-client flag.
  PAGE_AUTOMATION: 'ff-aio-150-page-automation',
  // Gates the sandbox-free WFS Sync pull/push API in the monolith
  // (`POST /api/ai/wfs/{pull,push}`). When OFF the routes respond 404
  // (surface hidden). Off by default.
  WFS_SYNC: 'ff-ext-951-wfs-sync',
  // EXT-2027: gates AIA uploading a user's attached image as a site asset.
  // Covers the original-file presign, the internal asset-create route, and
  // the `upload-asset` agent tool. Off by default.
  AIA_ASSET_UPLOAD: 'ff-ext-2027-aia-asset-upload',
  // SOUR-2515: gates starting Source Cloud agent runs. When OFF, admission
  // checks answer `allowed: false`, so status, results and cancellation keep
  // working. Off by default. The key skips the ff- naming because the Source
  // Cloud service already uses it for its local flags.
  SOURCE_CLOUD_AGENT_RUNTIME: 'source_cloud_agent_runtime',
  COMPATIBLE_WITH_DESIGN_ASSISTANT_TEMPLATE:
    'ff-apai-995-compatible-with-design-assistant',
  COPILOT: 'ff-apai-169-copilot',
  DISABLE_GOOGLE_ERROR_NORMALIZATION:
    'ff-aio-disable-google-error-normalization',
  // Kill switch for EXT-1215: when ON, GoogleAdapter keeps assistant text that
  // accompanies tool calls, which Gemini 3.6 rejects with an opaque 400.
  DISABLE_GOOGLE_TOOL_CALL_TEXT_STRIP:
    'ff-ext-1215-disable-google-tool-call-text-strip',
  DENY_ANTHROPIC_REQUESTS: 'ff-apai-1193-deny-anthropic-requests',
  DENY_GOOGLE_REQUESTS: 'ff-aio-99-deny-google-requests',
  GOOGLE_VERTEX_AI: 'ff-aia-308-google-vertex-ai',
  /**
   * When ON, LLMClient.new() falls back to the direct Anthropic SDK
   * (`ANTHROPIC_API_KEY` env) instead of AWS Bedrock for any service that
   * does not have a service-specific direct Anthropic API key configured.
   * Kill-switch / cut-over toggle for the AWS Bedrock sunset (AIO-242).
   */
  LLM_CLIENT_DISABLE_BEDROCK: 'ff-aio-242-llm-client-disable-bedrock',
  DATADOG_ONLY_LLM_OBSERVABILITY: 'ff-aio-463-datadog-only-llm-observability',
  DENY_OPENAI_REQUESTS: 'ff-apai-258-deny-openai-requests',
  DENY_OPENROUTER_REQUESTS: 'ff-apai-2247-deny-openrouter-requests',
  DISABLE_LOGIC_FLOWS: 'ff-apai-1270-disable-logic-flows',
  MOVE_APP_GEN: 'ff-apai-3258-move-app-gen',
  ORION_AGENT_MIGRATION: 'ff-apai-2044-multi-turn-agent-migration',
  ORION_EDIT_FILE_TOOL: 'ff-apai-2331-edit-file-tool',
  ORION_RICH_LOGGING: 'ff-apai-1784-rich-logging-orion',
  ORION_USAGE_TRACKING: 'ff-apai-1716-orion-usage-tracking',
  VIBE_CODE_COMPONENT_SCREENSHOT: 'ff-apai-2787-vcc-screenshot',
  // EXT-348: gates capturing an additional mobile-viewport (375px) screenshot
  // alongside the existing desktop capture, for the visual_responsiveness eval
  // scorer (EXT-349). Independent of VIBE_CODE_COMPONENT_SCREENSHOT so it can be
  // rolled out/killed without affecting the already-shipped desktop capture.
  VCC_MOBILE_SCREENSHOT: 'ff-ext-348-vcc-mobile-screenshot',
  VIBE_COMPONENT_ELEMENT_SELECTION:
    'ff-apai-2831-vibe-component-element-selection',
  CODE_COMPONENTS_TEXT_EDITING: 'ff-apai-3365-code-components-text-editing',
  // EXT-2212: gates editing code component metadata (name, description, group)
  // from Component settings, plus the shared reducer changes it depends on —
  // the CODE_EVALUATED display-name uniqueness skip and the sourceRevision
  // staleness guard, which otherwise run for every code component evaluation.
  AICC_METADATA_EDITS: 'ff-ext-2212-aicc-metadata-edits',
  // EXT-897: gates the sandbox build's source-loc Babel plugin (data-wf-loc-text
  // / data-wf-loc-kids / data-wf-src-rev stamping)
  VCC_BUILD_TIME_STAMPING: 'ff-ext-897-vcc-build-time-stamping',
  // EXT-965: gates deterministic instant apply for inline text edits on code
  // components (VccInstantTextApply). Pairs with VCC_BUILD_TIME_STAMPING —
  // enable both for the full experience: stamping produces the source-loc
  // data the engine's high-precision tiers consume. With stamping off,
  // instant apply still works but only via the whole-file unique-match tier.
  VCC_INSTANT_TEXT_APPLY: 'ff-ext-965-vcc-instant-text-apply',
  // EXT-2620: lets VccInstantTextApply write text that renders from a prop to
  // its same-file default (declareComponent `defaultValue` or a destructured
  // default), or to the preview-state prop it comes from (source.meta.states).
  // Requires VCC_INSTANT_TEXT_APPLY; off, such edits go to the assistant.
  VCC_TEXT_APPLY_PROP_DEFAULTS: 'ff-ext-2620-vcc-text-apply-prop-defaults',
  // EXT-1219: gates deterministic instant apply for Style Panel edits on code
  // components (VccInstantStyleApply). Requires VCC_STYLE_PANEL (which produces
  // the drafts) and pairs with VCC_BUILD_TIME_STAMPING — with stamping off no
  // element resolves, so every draft falls back to the "Send to AI Assistant"
  // flow. Drafts it can't write provably (responsive / pseudo-state edits,
  // expression-valued `style`, unstamped void elements) always fall back too.
  VCC_INSTANT_STYLE_APPLY: 'ff-ext-1219-vcc-instant-style-apply',
  // EXT-2393: runtime-only draft cascade for code-component style edits across
  // all (variant × scenario) islands. Requires VCC_BUILD_TIME_STAMPING.
  AICC_CROSS_STATE_STYLE_CASCADE: 'ff-ext-2393-vcc-cross-state-style-cascade',
  // EXT-1732: makes both instant applies wait for the user's confirmation.
  // With it off, a style draft is written to source ~800ms after the last
  // edit and a text draft the moment the inline editor closes, so the canvas
  // rebuilds while the user is still adjusting. With it on, neither fires on
  // its own: drafts stay in the queued-changes bar, and the deterministic
  // engines run when the user clicks apply, with whatever they decline
  // falling through to the assistant turn as it does today. Off by default —
  // enabling it changes only *when* an instant apply happens, never what it
  // is able to write.
  VCC_INSTANT_APPLY_REQUIRES_CONFIRM:
    'ff-ext-1732-vcc-instant-apply-requires-confirm',
  // EXT-1958: gates the progress surfaces for a confirm-time instant apply —
  // the assistant chat's card and status row for what the apply wrote, and the
  // canvas blur over the same window. Reported state only: it changes nothing
  // about what an instant apply writes or when. ANDed with
  // VCC_INSTANT_APPLY_REQUIRES_CONFIRM, since off the confirm path the engines
  // auto-fire on a debounce and a report per auto-fire would be noise rather
  // than feedback. Off by default.
  VCC_INSTANT_APPLY_PROGRESS: 'ff-ext-1958-vcc-instant-apply-progress',
  // EXT-870: auto-jumps the code editor to the line rendering the element selected
  // on the canvas, and tints it. Depends on VCC_BUILD_TIME_STAMPING (the stamps are
  // how the line is known) and on element selection being available; with stamping
  // off, no element resolves and the editor is left alone. Read-only editor
  // affordance — it changes no source, so it rolls out on its own curve.
  VCC_AUTOJUMP_TO_CODE_LINE: 'ff-ext-870-autojump-to-relevant-line-in-code',
  // EXT-1889: outlines every element a visual edit reaches — the instances a
  // `.map()` draws. Needs VCC_BUILD_TIME_STAMPING; inert without it. Purely
  // visual, so it rolls out on its own curve.
  VCC_REPEATED_ELEMENT_SELECTION: 'ff-ext-1889-vcc-repeated-element-selection',
  // Gates the AICC multi-state visual-editing feature end to end: the
  // Designer-side scenario reads (canvas grid, Navigator States section,
  // scenario-binding engine, frame tools), the `set-component-states` AI tool,
  // and the multi-state sections of the code-component system prompt. Off by
  // default; when off the feature is inert (stored `source.meta.states`
  // persists but is hidden) and publish output is unchanged.
  AICC_VISUAL_EDITING_STATES: 'ff-aicc-visual-editing-states',
  // EXT-179: gates VCC multi-file support (source file tree UI + multi-file
  // editing). Off by default. Part of the VCC - Multifile epic (EXT-168).
  VCC_MULTIFILE: 'ff-ext-179-vcc-multifile',
  AICC_EXACT_TEXT_EDIT: 'ff-ext-2633-aicc-exact-text-edit',
  // Kill-switch for VCC failover to a secondary model on a clear Anthropic
  // outage. Off by default; the failover path is inert until this is on.
  CODE_1194_VCC_ANTHROPIC_FALLBACK: 'ff-code-1194-vcc-anthropic-fallback',
  // Gates the "Add content" CMS collection picker in the AICC
  // (assistant-next / VCC) composer. Off by default.
  AICC_CMS_PICKER: 'ff-ext-796-aicc-cms-picker',
  // Gates structured ask-user-question clarification for AICC create/edit
  // (fullscreen creator shell + VCC clarification prompt). Off by default.
  AICC_CLARIFICATION: 'ff-code-aicc-clarification',
  // EXT-2359: gates "start from code" for AI Code Components. Off by default.
  AICC_START_FROM_CODE: 'ff-ext-2359-aicc-start-from-code',
  // When ON, ai-bridge's "Scope filter applied" log includes the actual
  // OAuth `grantedScopes` strings (sorted) for OAuth-bearer agent runs.
  // Default OFF. Temporary measurement instrument to size the live scope-
  // composition distribution for the slack agent before the step-4
  // per-operation enforcement gate lands. Remove this flag (and the log
  // field it gates) once the measurement window closes.
  LOG_GRANT_SCOPES: 'ff-aio-397-log-grant-scopes',
  // When ON, `/v1/page-automation/execute` enforces the resolved tool's
  // `requiredScopes` against the OAuth grant. Denials return 403 with
  // `OAuthInsufficientScope`. The `page_client:write` super-scope
  // carve-out is honoured via `isScopeRequirementSatisfied`. Default OFF
  // so the merge is inert; operators roll out gradually from the Statsig
  // console. Kill-switch remains once enforcement is at 100%.
  ENFORCE_PAGE_AUTOMATION_SCOPES: 'ff-aio-398-enforce-page-automation-scopes',
  // When ON, drops the coarse page_client:write gate on the AI-shard mount
  // (/api/ai/page-automation/*), leaving per-tool enforcement (ff-aio-398) as
  // the authority so narrowed grants work; /beta keeps the coarse gate.
  // Default OFF; per-request kill-switch. executeBatchHandler's
  // res.locals.coarseScopeGateRelaxed marker forces per-tool enforcement to
  // run whenever this flag skips the coarse gate, even with ff-aio-398 off —
  // flag ordering can no longer zero out /execute's scope enforcement. AIO-208.
  RELAX_PAGE_AUTOMATION_COARSE_GATE:
    'ff-aio-208-relax-page-automation-coarse-gate',
} as const;
